import { AlertColor, Box, Stack, Typography, IconButton, Slide } from '@mui/material';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

interface AlertProviderProps {
  children?: React.ReactNode;
}

export interface AlertStyles {
  fontSize?: string;
  textAlign?: 'left' | 'center' | 'right';
  width?: string;
}

interface SnackbarAlertItem {
  id: number;
  message: string;
  severity: AlertColor;
  styles?: AlertStyles;
}

const AlertContext = createContext((_message: string, _severity: AlertColor, _styles?: AlertStyles) => {});

export const useAlert = () => useContext(AlertContext);

function SlideTransition(props: TransitionProps & { children: React.ReactElement }) {
  return <Slide {...props} direction="left" />;
}

export function AlertProvider({ children }: AlertProviderProps) {
  const [alerts, setAlerts] = useState<SnackbarAlertItem[]>([]);
  const alertIdRef = useRef(0);
  const timeoutRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const dismissAlert = useCallback((id: number) => {
    const timeout = timeoutRefs.current[id];
    if (timeout) {
      clearTimeout(timeout);
      delete timeoutRefs.current[id];
    }

    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const showSnackbar = useCallback(
    (message: string, severity: AlertColor, styles?: AlertStyles) => {
      const id = ++alertIdRef.current;
      const nextItem: SnackbarAlertItem = { id, message, severity, styles };

      setAlerts((prev) => [...prev, nextItem]);

      timeoutRefs.current[id] = setTimeout(() => {
        dismissAlert(id);
      }, 5000);
    },
    [dismissAlert]
  );

  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current = {};
    };
  }, []);

  const getAlertIcon = (severity: AlertColor) => {
    switch (severity) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 28, color: '#d32f2f' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 28, color: '#ed6c02' }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 28, color: '#0288d1' }} />;
      default:
        return <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50' }} />;
    }
  };

  const getAlertColor = (severity: AlertColor) => {
    switch (severity) {
      case 'success':
        return { bg: '#f1f8f4', border: '#4caf50' };
      case 'error':
        return { bg: '#fef6f6', border: '#d32f2f' };
      case 'warning':
        return { bg: '#fff8f1', border: '#ed6c02' };
      case 'info':
        return { bg: '#f0f7fb', border: '#0288d1' };
      default:
        return { bg: '#f1f8f4', border: '#4caf50' };
    }
  };

  const getAlertTitle = (severity: AlertColor) => {
    switch (severity) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Information';
      default:
        return 'Information';
    }
  };

  return (
    <AlertContext.Provider value={showSnackbar}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          zIndex: 1400,
          mt: 6,
          mr: 2,
          pointerEvents: 'none'
        }}>
        <Stack spacing={1.5} alignItems="flex-end">
          {alerts.map((alertItem) => {
            const colors = getAlertColor(alertItem.severity);

            return (
              <Slide key={alertItem.id} in={true} direction="left" timeout={{ enter: 400, exit: 300 }} appear={true}>
                <Box
                  sx={{
                    minWidth: '350px',
                    maxWidth: '450px',
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    border: `2px solid ${colors.border}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: 'auto'
                  }}>
                  <Box
                    sx={{
                      backgroundColor: colors.bg,
                      px: 3,
                      py: 2,
                      borderBottom: `1px solid ${colors.border}20`
                    }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      {getAlertIcon(alertItem.severity)}
                      <Typography variant="h6" fontWeight={600} sx={{ flex: 1, color: 'text.primary' }}>
                        {getAlertTitle(alertItem.severity)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => dismissAlert(alertItem.id)}
                        sx={{ color: 'text.secondary' }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      px: 3,
                      py: 2.5
                    }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: alertItem.styles?.fontSize || '0.95rem',
                        textAlign: alertItem.styles?.textAlign || 'left',
                        color: 'text.primary',
                        lineHeight: 1.6
                      }}
                      dangerouslySetInnerHTML={{ __html: alertItem.message }}
                    />
                  </Box>
                </Box>
              </Slide>
            );
          })}
        </Stack>
      </Box>
    </AlertContext.Provider>
  );
}
